import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import SuperAdminLayout from '../SuperAdminLayout';
import { saRequest } from '../../../utils/superAdminAxiosCaller';

interface KnownIp {
    id: number;
    ip: string;
    first_seen_at: string;
    last_seen_at: string;
    revoked_at: string | null;
}

interface MeResponse {
    super_admin: { id: number; name: string; email: string };
}

function formatDate(iso: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

const KnownIpsPage: React.FC = () => {
    const history = useHistory();
    const [email, setEmail] = useState<string | null>(null);
    const [ips, setIps] = useState<KnownIp[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [pendingRevoke, setPendingRevoke] = useState<number | null>(null);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [meRes, ipsRes] = await Promise.all([
                saRequest<MeResponse>('GET', 'auth/me'),
                saRequest<{ ips: KnownIp[] }>('GET', 'auth/known-ips'),
            ]);
            setEmail(meRes.data.super_admin.email);
            setIps(ipsRes.data.ips || []);
        } catch (err: any) {
            if (err?.response?.status === 401) {
                // saRequest already cleared the token; explicitly bounce to login
                // because the component is already rendered past the RequireSuperAdmin guard.
                history.replace('/mika/ops/login');
                return;
            }
            setError(err?.response?.data?.message || 'Failed to load known IPs.');
        } finally {
            setLoading(false);
        }
    }, [history]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const handleRevoke = async (id: number) => {
        if (pendingRevoke !== null) return;
        const ok = window.confirm(
            'Revoke this IP? Any active session from this IP will be invalidated on its next request, and future logins from this IP will be blocked until re-added.',
        );
        if (!ok) return;

        setPendingRevoke(id);
        try {
            await saRequest('POST', `auth/known-ips/${id}/revoke`);
            await fetchAll();
        } catch (err: any) {
            if (err?.response?.status === 401) {
                history.replace('/mika/ops/login');
                return;
            }
            setError(err?.response?.data?.message || 'Revoke failed.');
        } finally {
            setPendingRevoke(null);
        }
    };

    const trustedCount = ips.filter(i => !i.revoked_at).length;
    const revokedCount = ips.length - trustedCount;

    return (
        <SuperAdminLayout currentEmail={email}>
            <div className="sa-page-header">
                <div>
                    <h1>Known IPs</h1>
                    <div style={{ color: 'var(--sa-text-tertiary)', fontSize: 'var(--sa-fs-sm)', marginTop: 4 }}>
                        IP addresses you've signed in from.{' '}
                        {ips.length > 0 && (
                            <>
                                <span className="sa-mono" style={{ color: 'var(--sa-success)' }}>{trustedCount} trusted</span>
                                {revokedCount > 0 && <>{', '}<span className="sa-mono" style={{ color: 'var(--sa-danger)' }}>{revokedCount} revoked</span></>}.
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="sa-card">
                <p style={{ color: 'var(--sa-text-secondary)', fontSize: 'var(--sa-fs-sm)', marginTop: 0, marginBottom: 'var(--sa-s5)' }}>
                    If you see an IP you don't recognise, revoke it immediately and rotate your password.
                    New-IP alerts are emailed to other super-admins on each first login from a new address.
                </p>

                {error && <div className="sa-error-banner">{error}</div>}

                {loading ? (
                    <table className="sa-table">
                        <thead>
                            <tr>
                                <th>IP</th>
                                <th>First seen</th>
                                <th>Last seen</th>
                                <th>Status</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {[0, 1, 2].map(i => (
                                <tr key={i}><td colSpan={5} style={{ padding: 0 }}><div className="sa-skeleton-row" /></td></tr>
                            ))}
                        </tbody>
                    </table>
                ) : ips.length === 0 ? (
                    <div className="sa-empty">
                        <div className="sa-empty-title">No known IPs yet</div>
                        <div className="sa-empty-hint">
                            Your current IP gets recorded once your next login completes.
                        </div>
                    </div>
                ) : (
                    <table className="sa-table">
                        <thead>
                            <tr>
                                <th style={{ width: '180px' }}>IP</th>
                                <th>First seen</th>
                                <th>Last seen</th>
                                <th style={{ width: '200px' }}>Status</th>
                                <th style={{ width: '120px', textAlign: 'right' }} />
                            </tr>
                        </thead>
                        <tbody>
                            {ips.map(ip => (
                                <tr key={ip.id}>
                                    <td>
                                        <span className="sa-mono" style={{ color: 'var(--sa-text)' }}>{ip.ip}</span>
                                    </td>
                                    <td style={{ color: 'var(--sa-text-tertiary)' }}>{formatDate(ip.first_seen_at)}</td>
                                    <td style={{ color: 'var(--sa-text-tertiary)' }}>{formatDate(ip.last_seen_at)}</td>
                                    <td>
                                        {ip.revoked_at ? (
                                            <span className="sa-badge sa-badge-inactive" style={{ color: 'var(--sa-danger)' }}>
                                                Revoked {formatDate(ip.revoked_at)}
                                            </span>
                                        ) : (
                                            <span className="sa-badge sa-badge-active">Trusted</span>
                                        )}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        {!ip.revoked_at && (
                                            <button
                                                type="button"
                                                className="sa-btn sa-btn-danger sa-btn-sm"
                                                disabled={pendingRevoke === ip.id}
                                                onClick={() => handleRevoke(ip.id)}
                                            >
                                                {pendingRevoke === ip.id && <span className="sa-btn-spinner" />}
                                                {pendingRevoke === ip.id ? 'Revoking…' : 'Revoke'}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </SuperAdminLayout>
    );
};

export default KnownIpsPage;
